import React from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import PeterChat from './PeterChat';
import { PeterMark } from './PeterAvatar';

// Same fallback the drawer used: unset, this component simply isn't mounted (see App.js), so there's
// nothing here to gate a second time.
const PETER_ENABLED = process.env.REACT_APP_PETER_ENABLED === 'true';

/**
 * Peter, as a floating widget available on every screen — the round button in the bottom-right
 * corner every app of this kind has, rather than something tucked inside Help.
 *
 * Mounted once in App.js, outside <Routes>, and for the app's entire lifetime: it is never
 * conditionally unmounted, including while collapsed. That's what makes a conversation survive both
 * minimizing the panel and navigating to another page — `open` only ever controls whether the panel
 * is the thing currently on screen, never whether PeterChat exists.
 *
 * `hidden` is for the one case two floating panels would otherwise occupy the same corner: while
 * Help is open. It hides via CSS on an ancestor, not a conditional return, for the same reason —
 * unmounting on every Help open/close would be a second way to lose the conversation.
 */
function PeterWidget({ open, onOpenChange, hidden, onCiteTopic }) {
  const { pathname } = useLocation();

  if (!PETER_ENABLED) return null;

  return (
    <div className={`peter-widget${hidden ? ' peter-widget--hidden' : ''}`}>
      <div className={`peter-panel${open ? '' : ' peter-panel--collapsed'}`} role="dialog" aria-modal="false" aria-label="Ask Peter" aria-hidden={!open}>
        <div className="peter-panel-head">
          <span className="peter-panel-title"><PeterMark size={20} /> Peter</span>
          <button
            type="button"
            className="peter-panel-close"
            onClick={() => onOpenChange(false)}
            aria-label="Minimize Peter"
            tabIndex={open ? 0 : -1}
          >
            <ChevronDown size={16} strokeWidth={2} aria-hidden />
          </button>
        </div>
        {/* Always rendered, never conditional on `open` — see the component doc above. */}
        <PeterChat route={pathname} onOpenTopic={onCiteTopic} active={open} />
      </div>

      <button
        type="button"
        className="peter-fab"
        onClick={() => onOpenChange(!open)}
        aria-label={open ? 'Minimize Peter' : 'Ask Peter'}
        aria-expanded={open}
      >
        <PeterMark size={36} />
      </button>
    </div>
  );
}

export default PeterWidget;
